import { useCallback, useEffect } from 'react';

import { getAccountTypeForConnection, productId } from '@trader/constants';
import { IMessage } from '@trader/types';
import { TInstrumentEntity, useMst } from '@trader/store';

import { EConnectionHub, EConnectionHubSubscriber } from './core/types';
import { useGetPageVisibility } from '../useGetPageVisibility';
import { useHub } from './core/useHub';
import { formatByPipSize, getSpreadDifference } from '@trader/utils';

export const useInstruments = (
  instrumentsIds: Array<string>,
  isTrading?: boolean
) => {
  const store = useMst();
  const isVisible = useGetPageVisibility();

  const isLoaded =
    isTrading || store.entities.instruments.getInstrumentsAsync.hasEverBeenRun;

  const activeTradingAccount = store.user.activeTradingAcc();

  const login = activeTradingAccount?.platformLogin;
  const accountType = activeTradingAccount?.accountType;

  const handleUpdatePrice = useCallback((message: IMessage) => {
    const currentInstrument = store.entities.instruments.get<TInstrumentEntity>(
      message.symbol
    );
    const { ask, bid } = getSpreadDifference(
      currentInstrument.spreadDiff,
      message.ask,
      message.bid
    );
    if (ask !== currentInstrument.ask) {
      store.entities.instruments.update(message.symbol, {
        updateAskType:
          formatByPipSize(ask, currentInstrument.pipSize) >
          formatByPipSize(currentInstrument.ask, currentInstrument.pipSize)
            ? 'up'
            : 'down',
        ask,
      });
    }

    if (bid !== currentInstrument.bid) {
      store.entities.instruments.update(message.symbol, {
        updateBidType:
          formatByPipSize(bid, currentInstrument.pipSize) >
          formatByPipSize(currentInstrument.bid, currentInstrument.pipSize)
            ? 'up'
            : 'down',
        bid,
      });
    }
  }, []);

  const { unsubscribe, subscribe } = useHub<IMessage>({
    url: `${import.meta.env.VITE_SIGNALR_URL}/v3/quotes`,
    hub: EConnectionHub.Quotes,
    subscriber: EConnectionHubSubscriber.Quotes,
    onMessage: handleUpdatePrice,
    invokedName: 'OnQuote',
    subscriptionArgs: {
      instrumentsIds: JSON.stringify(instrumentsIds),
      activeTradingAccount,
    },
  });

  const handleSubscribe = useCallback(() => {
    if (isLoaded && instrumentsIds.length && login && accountType) {
      subscribe(async connection => {
        await connection?.send(
          'SubscribeToQuotes',
          instrumentsIds,
          1,
          productId[import.meta.env.VITE_PRODUCT],
          login,
          getAccountTypeForConnection[accountType]
        );
      });
    }
  }, [isLoaded, instrumentsIds.length, activeTradingAccount]);

  const handleUnSubscribe = useCallback(() => {
    if (login && accountType) {
      unsubscribe(async connection => {
        await connection?.send('UnsubscribeFromAllQuotes');
      });
    }
  }, [login, accountType]);

  useEffect(() => {
    handleSubscribe();
    return () => {
      handleUnSubscribe();
    };
  }, [JSON.stringify(instrumentsIds), activeTradingAccount, isLoaded]);

  useEffect(() => {
    if (isVisible) {
      handleSubscribe();
    } else {
      handleUnSubscribe();
    }
  }, [isVisible]);
};
